import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { RON } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const { user, toggleWishlist } = useAuth();
  const inWishlist = user?.wishlist?.includes(product.slug);
  const out = product.stock_status === "out";

  const quickAdd = (e) => {
    e.preventDefault();
    if (out) return;
    add(product, product.grammages[product.grammages.length - 1], 1);
    toast.success(`${product.name} adăugat în coș`, { description: `${product.brand} · ${product.grammage}` });
  };

  const wish = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Autentifică-te pentru a salva favorite");
      return;
    }
    const active = await toggleWishlist(product.slug);
    toast[active ? "success" : "message"](active ? "Adăugat la favorite" : "Șters din favorite");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: Math.min(index, 8) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/produs/${product.slug}`} className="group block">
        <div className="relative overflow-hidden bg-stone aspect-[4/5]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105 ${out ? "grayscale opacity-70" : ""}`}
          />
          {/* badges */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {product.on_sale && (
              <span data-testid={`badge-sale-${product.slug}`} className="bg-roast text-cream text-[11px] font-semibold px-2.5 py-1 tracking-wide">
                −{product.discount_percent}%
              </span>
            )}
            {product.is_new && <span className="bg-ink text-cream text-[11px] font-semibold px-2.5 py-1 tracking-wide">NOU</span>}
            {product.bestseller && !product.is_new && <span className="bg-sand text-ink text-[11px] font-semibold px-2.5 py-1 tracking-wide">BESTSELLER</span>}
          </div>
          {out && (
            <div className="absolute inset-0 grid place-items-center">
              <span className="bg-ink/85 text-cream text-xs font-semibold px-4 py-2 tracking-wide">Stoc epuizat</span>
            </div>
          )}
          <button
            onClick={wish}
            data-testid={`wishlist-btn-${product.slug}`}
            className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-cream/85 backdrop-blur text-ink hover:bg-roast hover:text-cream transition-colors duration-300"
            aria-label="Favorite"
          >
            <Heart size={17} fill={inWishlist ? "currentColor" : "none"} />
          </button>
          {!out && (
            <button
              onClick={quickAdd}
              data-testid={`quick-add-${product.slug}`}
              className="absolute bottom-4 left-4 right-4 flex items-center justify-center gap-2 bg-ink text-cream py-3 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 text-sm font-medium"
            >
              <Plus size={16} /> Adaugă în coș
            </button>
          )}
        </div>
        <div className="pt-4">
          <div className="flex items-center justify-between">
            <p className="overline text-roast">{product.brand}</p>
            {product.stock_status === "limited" && (
              <span className="text-[11px] text-roast font-medium">Stoc limitat</span>
            )}
          </div>
          <h3 className="font-display text-2xl leading-tight mt-1">{product.name}</h3>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground capitalize">
              {product.type} · Intensitate {product.intensity}/{product.intensity_scale || 13}
            </span>
            <span className="flex items-baseline gap-2">
              {product.on_sale && <span className="text-xs text-muted-foreground line-through">{RON(product.old_price)}</span>}
              <span className={`font-medium ${product.on_sale ? "text-roast" : ""}`}>{RON(product.effective_price)}</span>
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
